'use client'

import { useEffect } from 'react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Locale } from '@/i18n'
import { useApplicationModalStore } from '@/stores/applicationModalStore'

import { CivilForm } from './CivilForm'
import { MilitaryForm } from './MilitaryForm'
import { getApplicationTabsTranslations } from './constants'
import './application-tabs.css'

interface ApplicationModalProps {
  locale: Locale
}

export function ApplicationModal({ locale }: ApplicationModalProps) {
  const { isOpen, applicationType, positionSlug, closeModal, setApplicationType } =
    useApplicationModalStore()

  const translations = getApplicationTabsTranslations(locale)

  const modalTitle = {
    uk: 'Приєднатися до команди',
    en: 'Join the Team',
  }[locale]

  const modalDescription = {
    uk: 'Оберіть тип заявки та заповніть форму',
    en: 'Select application type and fill out the form',
  }[locale]

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="flex flex-col w-full max-h-[90vh] rounded-t-2xl rounded-b-none sm:rounded-lg sm:max-w-[900px] sm:max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border-gray-800">
        <DialogHeader>
          <DialogTitle className="text-xl">{modalTitle}</DialogTitle>
          <DialogDescription>{modalDescription}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={applicationType}
          onValueChange={(value) => setApplicationType(value as 'civil' | 'military')}
        >
          <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#1a1a1a]">
            <TabsTrigger value="civil" className={applicationType === 'civil' ? 'civil-bg' : ''}>
              {translations.civil}
            </TabsTrigger>
            <TabsTrigger
              value="military"
              className={applicationType === 'military' ? 'military-bg' : ''}
            >
              {translations.military}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="civil">
            <div className="civil-bg rounded-lg p-4">
              <CivilForm locale={locale} positionSlug={positionSlug || undefined} />
            </div>
          </TabsContent>
          <TabsContent value="military">
            <div className="military-bg rounded-lg p-4">
              <MilitaryForm locale={locale} positionSlug={positionSlug || undefined} />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
